import { createContext, useContext, useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const SiteSettingsContext = createContext(null);

/**
 * Provides the public site_settings document to every cart-aware
 * component. One fetch on mount; re-fetches when explicitly refreshed
 * (e.g. after the admin saves changes).
 *
 * Default values mirror the backend defaults so the UI still renders
 * usefully if the fetch is in-flight or fails.
 */
const DEFAULTS = {
  promo_banner_enabled: false,
  promo_banner_text: "",
  promo_banner_cta_label: "",
  promo_banner_cta_url: "",
  free_shipping_threshold: 50.0,
  gift_promotion_enabled: false,
  gift_promotion_min_subtotal: 150.0,
  gift_promotion_sku: "PL-GHKCU-50",
  spotlight_sku: "PL-RET-30",
};

export function SiteSettingsProvider({ children }) {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const refresh = () => {
    fetch(`${API}/api/site-settings`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setCfg({ ...DEFAULTS, ...data });
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SiteSettingsContext.Provider value={{ ...cfg, loaded, refresh }}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export const useSiteSettings = () => {
  const ctx = useContext(SiteSettingsContext);
  // Fallback if a component renders outside the provider (e.g. unit
  // tests) — return defaults so nothing throws.
  return ctx || { ...DEFAULTS, loaded: false, refresh: () => {} };
};
